import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PrintSessionBanner from "@/components/PrintSessionBanner";
import { usePrintPage } from "@/lib/usePrintPage";
import { Printer, ArrowLeft } from "lucide-react";

/**
 * Gezamenlijke RAAPLIJST voor de kas/schuur — gegroepeerd per locatie.
 * Bonnen kunnen per locatie geprint worden.
 */
export default function CombinedPickList() {
    usePrintPage("a4");
    const loc = useLocation();
    const nav = useNavigate();
    const params0 = new URLSearchParams(loc.search);
    const initialLocFilter = params0.get("location_id") || "";
    const requestedLocationIds = useMemo(() => {
        const params = new URLSearchParams(loc.search);
        const legacyLocationId = params.get("location_id") || "";
        return Array.from(new Set(
            (params.get("location_ids") || "")
                .split(",")
                .map((value) => value.trim())
                .filter(Boolean)
                .concat(legacyLocationId ? [legacyLocationId] : [])
        ));
    }, [loc.search]);
    const autoPrint = params0.get("auto_print") === "1";
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [filter, setFilter] = useState(initialLocFilter); // location_id filter
    const [locMap, setLocMap] = useState({}); // id → location object (color, name)
    const [pendingPrintFilter, setPendingPrintFilter] = useState(null);
    const autoPrinted = useRef(false);

    useEffect(() => {
        api.get("/locations")
            .then((r) => setLocMap(Object.fromEntries(r.data.map((l) => [l.id, l]))))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(loc.search);
        const idsStr = params.get("ids") || "";
        const ids = idsStr.split(",").filter(Boolean);
        if (ids.length === 0) {
            setErr("Geen bestellingen geselecteerd");
            return;
        }
        const activeLocationIds = filter ? [filter] : requestedLocationIds;
        const q = activeLocationIds.length
            ? `?location_ids=${encodeURIComponent(activeLocationIds.join(","))}`
            : "";
        setData(null);
        setErr("");
        api.post(`/pick-list${q}`, { order_ids: ids })
            .then((r) => setData(r.data))
            .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail ?? "Laden mislukt")));
    }, [loc.search, filter, requestedLocationIds]);

    // Auto-print when launched from Printcentrum (?auto_print=1).
    useEffect(() => {
        if (!autoPrint || !data || autoPrinted.current) return;
        autoPrinted.current = true;
        const t = setTimeout(() => window.print(), 400);
        return () => clearTimeout(t);
    }, [autoPrint, data]);

    useEffect(() => {
        if (!data || pendingPrintFilter === null || pendingPrintFilter !== filter) return;
        setPendingPrintFilter(null);
        const t = setTimeout(() => window.print(), 0);
        return () => clearTimeout(t);
    }, [data, filter, pendingPrintFilter]);

    if (err) {
        return (
            <div className="p-12 max-w-3xl mx-auto">
                <button
                    onClick={() => nav("/bestellingen")}
                    className="text-sm text-ink-500 hover:text-florefy-900 mb-4 flex items-center gap-1"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug
                </button>
                <div className="bg-destructive/10 border border-destructive text-destructive text-sm p-4 rounded-md">
                    {err}
                </div>
            </div>
        );
    }
    if (!data) return <div className="p-12 text-ink-500">Raaplijst opbouwen…</div>;

    const groups = data.by_location || [];
    const locations = Array.from(
        new Map(
            groups.map((g) => [
                g.location_id,
                { id: g.location_id, code: g.location_code, name: g.location_name },
            ])
        ).values()
    );

    return (
        <div>
            <PrintSessionBanner />
            <div className="p-8 lg:p-12 max-w-3xl no-print mx-auto">
                <button
                    onClick={() => nav("/bestellingen")}
                    className="text-sm text-ink-500 hover:text-florefy-900 mb-4 flex items-center gap-1"
                    data-testid="btn-back-to-orders"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug naar bestellingen
                </button>
                <PageHeader
                    kicker={`Raaplijst voor de kas · ${data.totals.total_orders} bestellingen`}
                    title={`${data.totals.total_products} planten · ${data.totals.total_units} stuks totaal`}
                    subtitle={
                        data.delivery_dates.length
                            ? `Leverdatum(s): ${data.delivery_dates.join(", ")} · ${data.totals.total_locations || groups.length} locatie${(data.totals.total_locations || groups.length) === 1 ? "" : "s"}`
                            : ""
                    }
                    testid="picklist-header"
                    actions={
                        <button
                            onClick={() => window.print()}
                            data-testid="btn-print-picklist"
                            className="px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2"
                        >
                            <Printer className="h-4 w-4" /> Print {filter ? "locatie" : "alles"}
                        </button>
                    }
                />

                <div className="flex flex-wrap gap-2 mb-6" data-testid="picklist-location-filter">
                    <button
                        onClick={() => setFilter("")}
                        className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                            !filter
                                ? "bg-florefy-900 text-white border-florefy-900"
                                : "bg-white text-ink-700 border-border hover:bg-ink-50"
                        }`}
                        data-testid="picklist-filter-all"
                    >
                        {requestedLocationIds.length > 1 ? "Alle geselecteerde locaties" : "Alle locaties"}
                    </button>
                    {locations.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => setFilter(l.id)}
                            className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                                filter === l.id
                                    ? "bg-florefy-900 text-white border-florefy-900"
                                    : "bg-white text-ink-700 border-border hover:bg-ink-50"
                            }`}
                            data-testid={`picklist-filter-${l.code}`}
                        >
                            {l.code} · {l.name}
                        </button>
                    ))}
                </div>

                {groups.map((g) => {
                    const color = locMap[g.location_id]?.color || "#94a3b8";
                    return (
                        <div
                            key={g.location_id || "none"}
                            className="mb-8 bg-white border border-border rounded-md overflow-hidden shadow-sm"
                            style={{ borderLeft: `6px solid ${color}` }}
                            data-testid={`picklist-location-block-${g.location_code}`}
                        >
                            <div
                                className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border"
                                style={{ backgroundColor: color + "1f" }}
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className="h-9 w-9 rounded-sm border border-black/20 flex items-center justify-center font-mono font-bold text-ink-950 text-base shadow-sm"
                                        style={{ backgroundColor: color }}
                                    >
                                        {g.location_code}
                                    </div>
                                    <div>
                                        <div className="text-base font-semibold text-ink-900 leading-tight">
                                            {g.location_name}
                                        </div>
                                        <div className="font-mono text-[11px] uppercase tracking-widest text-ink-500 mt-0.5">
                                            {g.items.length} product{g.items.length === 1 ? "" : "en"} · {g.total_quantity} stuks
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setData(null);
                                        setFilter(g.location_id);
                                        setPendingPrintFilter(g.location_id);
                                    }}
                                    className="px-3 py-1.5 rounded-sm border border-border bg-white hover:bg-ink-50 text-xs font-medium flex items-center gap-2"
                                    data-testid={`btn-print-picklist-location-${g.location_code}`}
                                    title={`Print alleen ${g.location_code}`}
                                >
                                    <Printer className="h-3.5 w-3.5" /> Print {g.location_code}
                                </button>
                            </div>
                            <PickList items={g.items} totalUnits={g.total_quantity} embedded />
                        </div>
                    );
                })}
                {groups.length === 0 && (
                    <div className="text-sm text-ink-500 py-8 text-center" data-testid="picklist-empty">
                        Geen regels in deze selectie.
                    </div>
                )}
            </div>

            <div className="print-only">
                {groups.map((g) => (
                    <div key={g.location_id || "none"} className="print-page p-8 bg-white text-black">
                        <div className="border-b-2 border-black pb-3 mb-5">
                            <div className="font-mono text-[10px] uppercase tracking-widest">
                                Florefy Flow · Raaplijst voor de kas
                            </div>
                            <div className="text-2xl font-bold mt-1">
                                {g.items.length} planten · {g.total_quantity} stuks
                            </div>
                            <div className="text-sm mt-1">
                                {data.totals.total_orders} bestellingen
                                {data.delivery_dates.length
                                    ? ` · leverdatum(s): ${data.delivery_dates.join(", ")}`
                                    : ""}
                            </div>
                            <div className="mt-2 inline-block px-2 py-1 border-2 border-black font-mono text-sm font-bold">
                                LOCATIE {g.location_code} · {g.location_name}
                            </div>
                        </div>
                        <PickList items={g.items} totalUnits={g.total_quantity} print />
                        <div className="mt-4 text-[10px] font-mono">
                            Florefy Flow — Gegenereerd op {new Date().toLocaleString("nl-NL")}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function qualityLabel(it) {
    const qs = it.qualities || [];
    if (qs.length === 0) return null;
    if (qs.length === 1) return { mode: "single", value: qs[0] };
    // Meerdere kwaliteiten → tel per kwaliteit op
    const sum = {};
    for (const pc of it.per_customer || []) {
        sum[pc.quality] = (sum[pc.quality] || 0) + (pc.quantity || 0);
    }
    return {
        mode: "split",
        parts: ["A+", "A", "B"].filter((q) => sum[q]).map((q) => ({ q, n: sum[q] })),
    };
}

function QualityBadge({ value, print }) {
    const tone =
        value === "A+"
            ? "bg-florefy-900 text-white border-florefy-900"
            : value === "B"
            ? "bg-ink-200 text-ink-800 border-ink-300"
            : "bg-florefy-100 text-florefy-900 border-florefy-200";
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-sm font-mono text-xs font-bold border ${
                print ? "border-2 border-black bg-white text-black" : tone
            }`}
            data-testid={`quality-badge-${value}`}
        >
            {value}
        </span>
    );
}

function PickList({ items, totalUnits, print = false, embedded = false }) {
    const wrapperCls = print
        ? "border-2 border-black"
        : embedded
        ? "overflow-x-auto"
        : "bg-white border border-border rounded-md overflow-x-auto";
    return (
        <div className={wrapperCls}>
            <table className="w-full">
                <thead>
                    <tr className={print ? "border-y-2 border-black bg-white text-black" : "bg-ink-50"}>
                        <th className="text-left px-4 py-3 data-label">Plant</th>
                        <th className="text-left px-4 py-3 data-label">Pot</th>
                        <th className="text-left px-4 py-3 data-label">Liter</th>
                        <th className="text-left px-4 py-3 data-label">Kwal.</th>
                        <th className="text-right px-4 py-3 data-label">Totaal</th>
                        {!print && <th className="w-10 px-4 py-3"></th>}
                    </tr>
                </thead>
                <tbody>
                    {items.map((it) => {
                        const ql = qualityLabel(it);
                        return (
                        <tr
                            key={`${it.product_id}-${it.location_id}`}
                            className={
                                print
                                    ? "border-t border-black"
                                    : "border-t border-border/60 hover:bg-ink-50"
                            }
                            data-testid={`picklist-row-${it.product_id}`}
                        >
                            <td className="px-4 py-3">
                                <div className="font-semibold text-ink-900 text-base">{it.name}</div>
                                {it.cultivar && (
                                    <div className="italic text-ink-500 text-sm">{it.cultivar}</div>
                                )}
                            </td>
                            <td className="px-4 py-3 font-mono">{it.pot_size || "—"}</td>
                            <td className="px-4 py-3 font-mono">{it.pot_liters || "—"}</td>
                            <td className="px-4 py-3" data-testid={`picklist-quality-${it.product_id}`}>
                                {!ql ? (
                                    <span className="text-ink-400 text-xs">—</span>
                                ) : ql.mode === "single" ? (
                                    <QualityBadge value={ql.value} print={print} />
                                ) : (
                                    <div className="flex flex-wrap gap-1 items-center">
                                        {ql.parts.map((p) => (
                                            <span
                                                key={p.q}
                                                className="inline-flex items-center gap-1"
                                            >
                                                <QualityBadge value={p.q} print={print} />
                                                <span className="font-mono text-xs text-ink-700">
                                                    {p.n}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </td>
                            <td className="px-4 py-3 font-mono font-bold text-2xl text-right text-ink-900">
                                {it.total_quantity}
                            </td>
                            {!print && (
                                <td className="px-4 py-3 text-right">
                                    <input
                                        type="checkbox"
                                        title="Aangevinkt = afgerond"
                                        className="cursor-pointer"
                                    />
                                </td>
                            )}
                        </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr
                        className={
                            print
                                ? "bg-gray-100 border-t-2 border-black"
                                : "bg-ink-50 border-t-2 border-ink-900"
                        }
                    >
                        <td colSpan={4} className="px-4 py-3 font-bold uppercase">Totaal</td>
                        <td className="px-4 py-3 font-mono text-right font-bold text-xl">{totalUnits}</td>
                        {!print && <td></td>}
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}
